import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, Pencil, Trash2, Calendar, Clock, User, Users, 
  CheckCircle2, Circle, AlertCircle, Loader2, MessageSquare,
  ChevronDown, Filter, Search, X, Flag, Settings, GripVertical, Save, RotateCcw, ListFilter,
  RefreshCw, Wifi, WifiOff
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Checkbox } from './ui/checkbox';
import { 
  getTasks, createTask, updateTask, deleteTask, getTasksStats,
  getTaskStatuses, createTaskStatus, updateTaskStatus, deleteTaskStatus
} from '../services/api';

// LocalStorage key for saved filter preferences
const TASK_FILTERS_KEY = 'gelatti_task_filters';

const priorityOptions = [
  { value: 'baixa', label: 'Baixa', color: '#64748b', icon: Flag },
  { value: 'media', label: 'Média', color: '#f59e0b', icon: Flag },
  { value: 'alta', label: 'Alta', color: '#ef4444', icon: Flag }
];

const defaultColors = [
  '#64748b', '#3b82f6', '#f59e0b', '#10b981', '#ef4444', 
  '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316'
];

const getPriorityInfo = (priority) => priorityOptions.find(p => p.value === priority) || priorityOptions[1];


// Status Configuration Modal
const StatusConfigModal = ({ isOpen, statuses, onClose, onSave, onDelete }) => {
  const [localStatuses, setLocalStatuses] = useState([]);
  const [newStatus, setNewStatus] = useState({ name: '', color: '#64748b', is_final: false });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocalStatuses(statuses || []);
  }, [statuses]);

  const handleAdd = async () => {
    if (!newStatus.name.trim()) return;
    setSaving(true);
    try {
      await onSave(newStatus);
      setNewStatus({ name: '', color: '#64748b', is_final: false });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (status) => {
    setSaving(true);
    try {
      await onSave(status, status.id);
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (statusId) => {
    if (!window.confirm('Excluir este status?')) return;
    try {
      await onDelete(statusId);
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao excluir status');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configurar Status das Tarefas</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Status List */}
          <div className="space-y-2">
            {localStatuses.map((status, index) => (
              <div 
                key={status.id}
                className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
              >
                <GripVertical className="w-4 h-4 text-slate-300" />
                
                {editingId === status.id ? (
                  <>
                    <input
                      type="color"
                      value={status.color}
                      onChange={(e) => setLocalStatuses(prev => 
                        prev.map(s => s.id === status.id ? {...s, color: e.target.value} : s)
                      )}
                      className="w-8 h-8 rounded cursor-pointer"
                    />
                    <Input
                      value={status.name}
                      onChange={(e) => setLocalStatuses(prev =>
                        prev.map(s => s.id === status.id ? {...s, name: e.target.value} : s)
                      )}
                      className="flex-1"
                    />
                    <label className="flex items-center gap-1 text-xs">
                      <input
                        type="checkbox"
                        checked={status.is_final}
                        onChange={(e) => setLocalStatuses(prev =>
                          prev.map(s => s.id === status.id ? {...s, is_final: e.target.checked} : s)
                        )}
                      />
                      Final
                    </label>
                    <Button size="sm" onClick={() => handleUpdate(status)} disabled={saving}>
                      <CheckCircle2 className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <div 
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: status.color }}
                    />
                    <span className="flex-1 font-medium">{status.name}</span>
                    {status.is_final && (
                      <span className="text-xs bg-slate-200 px-2 py-0.5 rounded">Final</span>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => setEditingId(status.id)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => handleDelete(status.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Add New Status */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-sm text-slate-700 mb-2">Adicionar Novo Status</h4>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={newStatus.color}
                onChange={(e) => setNewStatus(prev => ({ ...prev, color: e.target.value }))}
                className="w-10 h-10 rounded cursor-pointer"
              />
              <Input
                placeholder="Nome do status"
                value={newStatus.name}
                onChange={(e) => setNewStatus(prev => ({ ...prev, name: e.target.value }))}
                className="flex-1"
              />
              <label className="flex items-center gap-1 text-sm whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={newStatus.is_final}
                  onChange={(e) => setNewStatus(prev => ({ ...prev, is_final: e.target.checked }))}
                />
                Final
              </label>
              <Button onClick={handleAdd} disabled={saving || !newStatus.name.trim()}>
                <Plus className="w-4 h-4 mr-1" />
                Adicionar
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Status "Final" indica que a tarefa foi concluída ou cancelada.
            </p>
          </div>
        </div>

        <div className="flex justify-end mt-4 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Task Card Component
const TaskCard = ({ task, users, statuses = [], onEdit, onDelete, onStatusChange }) => {
  const status = (statuses || []).find(s => s.id === task.status) || { name: task.status, color: '#64748b' };
  const priority = getPriorityInfo(task.priority);
  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && !status.is_final;
  
  const assignedUserNames = task.assigned_users?.map(userId => {
    const user = users.find(u => u.id === userId);
    return user?.name || 'Usuário';
  }) || [];

  const formatDate = (date) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  };

  return (
    <div className={`bg-white rounded-xl border ${isOverdue ? 'border-red-300 bg-red-50' : 'border-slate-200'} p-4 hover:shadow-md transition-shadow`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span 
              className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
              style={{ backgroundColor: status.color }}
            >
              {status.name}
            </span>
            <span 
              className="flex items-center gap-1 text-xs"
              style={{ color: priority.color }}
            >
              <Flag className="w-3 h-3" />
              {priority.label}
            </span>
          </div>
          <h3 className="font-semibold text-slate-800">{task.title}</h3>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onEdit(task)}
            className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <Pencil className="w-4 h-4 text-slate-400" />
          </button>
          <button
            onClick={() => onDelete(task)}
            className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4 text-red-400" />
          </button>
        </div>
      </div>
      
      {task.description && (
        <p className="text-sm text-slate-600 mb-3 line-clamp-2">{task.description}</p>
      )}
      
      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
        {task.due_date && (
          <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-600 font-medium' : ''}`}>
            <Calendar className="w-3.5 h-3.5" />
            {formatDate(task.due_date)}
            {isOverdue && <AlertCircle className="w-3.5 h-3.5" />}
          </span>
        )}
        
        {assignedUserNames.length > 0 && (
          <span className="flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            {assignedUserNames.length === 1 ? assignedUserNames[0] : `${assignedUserNames.length} pessoas`}
          </span>
        )}
        
        {task.comments?.length > 0 && (
          <span className="flex items-center gap-1">
            <MessageSquare className="w-3.5 h-3.5" />
            {task.comments.length}
          </span>
        )}
        
        {task.created_by_name && (
          <span className="flex items-center gap-1 text-slate-400" title="Criado por">
            <User className="w-3.5 h-3.5" />
            {task.created_by_name}
          </span>
        )}
        
        {task.created_at && (
          <span className="flex items-center gap-1 text-slate-400" title="Data de criação">
            <Clock className="w-3.5 h-3.5" />
            {formatDate(task.created_at)}
          </span>
        )}
      </div>
      
      {/* Quick Status Change */}
      <div className="mt-3 pt-3 border-t border-slate-100">
        <select
          value={task.status}
          onChange={(e) => onStatusChange(task.id, e.target.value)}
          className="text-xs border rounded-lg px-2 py-1 w-full"
          style={{ borderColor: status.color }}
        >
          {(statuses || []).map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>
    </div>
  );
};

// Task Modal Component
const TaskModal = ({ isOpen, task, users, statuses = [], onClose, onSave, prefilledContact, onClearContact }) => {
  const [form, setForm] = useState({
    title: '',
    description: '',
    due_date: '',
    status: 'pendente',
    priority: 'media',
    assigned_users: [],
    related_contact_id: null,
    related_contact_name: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (task) {
      setForm({
        title: task.title || '',
        description: task.description || '',
        due_date: task.due_date ? new Date(task.due_date).toISOString().slice(0, 16) : '',
        status: task.status || (statuses?.[0]?.id || 'pendente'),
        priority: task.priority || 'media',
        assigned_users: task.assigned_users || [],
        related_contact_id: task.related_contact_id || null,
        related_contact_name: task.related_contact_name || ''
      });
    } else if (prefilledContact) {
      // Creating new task from CRM contact
      setForm({
        title: `Tarefa: ${prefilledContact.name}`,
        description: prefilledContact.summary || `Contato: ${prefilledContact.phone}`,
        due_date: '',
        status: statuses?.[0]?.id || 'pendente',
        priority: 'media',
        assigned_users: [],
        related_contact_id: prefilledContact.id,
        related_contact_name: prefilledContact.name
      });
    } else {
      setForm({
        title: '',
        description: '',
        due_date: '',
        status: statuses?.[0]?.id || 'pendente',
        priority: 'media',
        assigned_users: [],
        related_contact_id: null,
        related_contact_name: ''
      });
    }
  }, [task, statuses, prefilledContact]);

  const handleClose = () => {
    if (onClearContact) onClearContact();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      alert('Título é obrigatório');
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        ...form,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null
      };
      await onSave(data);
      if (onClearContact) onClearContact();
      onClose();
    } catch (error) {
      alert('Erro ao salvar tarefa');
    } finally {
      setSaving(false);
    }
  };

  const toggleUser = (userId) => {
    setForm(prev => ({
      ...prev,
      assigned_users: prev.assigned_users.includes(userId)
        ? prev.assigned_users.filter(id => id !== userId)
        : [...prev.assigned_users, userId]
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{task ? 'Editar Tarefa' : 'Nova Tarefa'}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Show linked contact if any */}
          {form.related_contact_name && (
            <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center gap-2 text-sm">
                <Users className="w-4 h-4 text-purple-600" />
                <span className="text-purple-800">
                  Vinculada ao contato: <strong>{form.related_contact_name}</strong>
                </span>
              </div>
            </div>
          )}

          {/* Show creator info when editing */}
          {task && (task.created_by_name || task.created_at) && (
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
              <div className="flex items-center justify-between text-sm text-slate-600">
                {task.created_by_name && (
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-500" />
                    <span>Criado por: <strong>{task.created_by_name}</strong></span>
                  </div>
                )}
                {task.created_at && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-slate-500" />
                    <span>{new Date(task.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Título *</label>
            <Input
              value={form.title}
              onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Ex: Preparar orçamento para cliente X"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Descrição</label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              placeholder="Detalhes da tarefa..."
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Data Prevista</label>
              <Input
                type="datetime-local"
                value={form.due_date}
                onChange={(e) => setForm(prev => ({ ...prev, due_date: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Prioridade</label>
              <select
                value={form.priority}
                onChange={(e) => setForm(prev => ({ ...prev, priority: e.target.value }))}
                className="w-full h-10 px-3 border border-slate-200 rounded-lg"
              >
                {priorityOptions.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
            <select
              value={form.status}
              onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))}
              className="w-full h-10 px-3 border border-slate-200 rounded-lg"
            >
              {(statuses || []).map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Responsáveis</label>
            <div className="flex flex-wrap gap-2">
              {users.map(user => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => toggleUser(user.id)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    form.assigned_users.includes(user.id)
                      ? 'bg-teal-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {user.name}
                </button>
              ))}
            </div>
            {form.assigned_users.length === 0 && (
              <p className="text-xs text-slate-400 mt-1">Clique para selecionar responsáveis</p>
            )}
          </div>
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white">
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Main Tasks Section Component
const TasksSection = ({ users, contactForNewTask, onTaskCreatedFromContact, currentUser, taskToOpen, onTaskOpened, wsConnected }) => {
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [prefilledContact, setPrefilledContact] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Load saved filters from localStorage or use defaults
  const getSavedFilters = () => {
    try {
      const saved = localStorage.getItem(TASK_FILTERS_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading saved filters:', e);
    }
    return null;
  };

  const savedFilters = getSavedFilters();
  const [filterStatuses, setFilterStatuses] = useState(savedFilters?.filterStatuses || []); // Changed to array for multi-select
  const [filterPriority, setFilterPriority] = useState(savedFilters?.filterPriority || 'all');
  const [filterUsers, setFilterUsers] = useState(savedFilters?.filterUsers || []); // Changed to array for multi-select
  const [searchTerm, setSearchTerm] = useState(''); // Search term is not saved
  const [viewMode, setViewMode] = useState(savedFilters?.viewMode || 'kanban');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [showUserFilter, setShowUserFilter] = useState(false); // For dropdown visibility
  const [showStatusFilter, setShowStatusFilter] = useState(false); // For status dropdown visibility
  
  // Column widths state - load from localStorage
  const savedColumnWidths = JSON.parse(localStorage.getItem('task_column_widths') || 'null');
  const defaultColumnWidths = { titulo: 280, criadoPor: 80, data: 80, responsaveis: 150, status: 120, acoes: 80 };
  const [columnWidths, setColumnWidths] = useState(savedColumnWidths || defaultColumnWidths);
  const [resizing, setResizing] = useState(null);

  // Save column widths to localStorage
  const saveColumnWidths = (newWidths) => {
    setColumnWidths(newWidths);
    localStorage.setItem('task_column_widths', JSON.stringify(newWidths));
  };

  // Handle column resize
  const handleMouseDown = (e, column) => {
    e.preventDefault();
    setResizing({ column, startX: e.clientX, startWidth: columnWidths[column] });
  };

  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e) => {
      const diff = e.clientX - resizing.startX;
      const newWidth = Math.max(50, resizing.startWidth + diff); // Minimum 50px
      setColumnWidths(prev => ({ ...prev, [resizing.column]: newWidth }));
    };

    const handleMouseUp = () => {
      if (resizing) {
        saveColumnWidths(columnWidths);
      }
      setResizing(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing, columnWidths]);

  // Reset column widths to default
  const resetColumnWidths = () => {
    saveColumnWidths(defaultColumnWidths);
  };
  
  // Handle contact for new task (coming from CRM)
  useEffect(() => {
    if (contactForNewTask) {
      setPrefilledContact(contactForNewTask);
      setEditingTask(null);
      setModalOpen(true);
    }
  }, [contactForNewTask]);

  // Handle opening a specific task from notification
  useEffect(() => {
    if (taskToOpen && tasks.length > 0) {
      const task = tasks.find(t => t.id === taskToOpen);
      if (task) {
        setEditingTask(task);
        setModalOpen(true);
        // Clear the taskToOpen after opening
        if (onTaskOpened) onTaskOpened();
      }
    }
  }, [taskToOpen, tasks, onTaskOpened]);

  // Toggle status filter
  const toggleStatusFilter = (statusName) => {
    setFilterStatuses(prev => 
      prev.includes(statusName) 
        ? prev.filter(s => s !== statusName)
        : [...prev, statusName]
    );
  };

  // Check if current filters differ from saved
  const checkForChanges = () => {
    const saved = getSavedFilters();
    if (!saved) {
      // If nothing saved, check against defaults
      return filterStatuses.length > 0 || filterPriority !== 'all' || filterUsers.length > 0 || viewMode !== 'kanban';
    }
    return JSON.stringify(filterStatuses) !== JSON.stringify(saved.filterStatuses || []) || 
           filterPriority !== saved.filterPriority || 
           JSON.stringify(filterUsers) !== JSON.stringify(saved.filterUsers || []) || 
           viewMode !== saved.viewMode;
  };

  useEffect(() => {
    setHasUnsavedChanges(checkForChanges());
  }, [filterStatuses, filterPriority, filterUsers, viewMode]);

  const handleSaveFilters = () => {
    const filters = {
      filterStatuses,
      filterPriority,
      filterUsers,
      viewMode
    };
    localStorage.setItem(TASK_FILTERS_KEY, JSON.stringify(filters));
    setHasUnsavedChanges(false);
    setSaveMessage('Filtros salvos!');
    setTimeout(() => setSaveMessage(''), 2000);
  };

  const handleResetFilters = () => {
    setFilterStatuses([]);
    setFilterPriority('all');
    setFilterUsers([]);
    setViewMode('kanban');
    setSearchTerm('');
    localStorage.removeItem(TASK_FILTERS_KEY);
    setHasUnsavedChanges(false);
  };

  // Toggle user in filter
  const toggleUserFilter = (userId) => {
    setFilterUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const fetchData = async () => {
    try {
      const [tasksData, statsData, statusesData] = await Promise.all([
        getTasks(),
        getTasksStats(),
        getTaskStatuses()
      ]);
      setTasks(tasksData);
      setStats(statsData);
      setStatuses(statusesData);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Manual refresh function
  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    await fetchData();
  };

  // Listen for WebSocket task updates
  useEffect(() => {
    const handleTaskUpdate = (event) => {
      const { event: eventType, data } = event.detail;
      
      if (eventType === 'created' && data?.task) {
        setTasks(prev => [data.task, ...prev]);
      } else if (eventType === 'updated' && data?.task) {
        setTasks(prev => prev.map(t => t.id === data.task.id ? data.task : t));
      } else if (eventType === 'deleted' && data?.task_id) {
        setTasks(prev => prev.filter(t => t.id !== data.task_id));
      }
    };

    window.addEventListener('task-update', handleTaskUpdate);
    return () => window.removeEventListener('task-update', handleTaskUpdate);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const handleSave = async (data) => {
    if (editingTask) {
      await updateTask(editingTask.id, data);
    } else {
      // Add created_by info for new tasks
      const taskData = {
        ...data,
        created_by: currentUser?.id,
        created_by_name: currentUser?.name || currentUser?.username
      };
      await createTask(taskData);
    }
    fetchData();
  };

  // Update task inline (for list view edits)
  const handleInlineUpdate = async (taskId, updateData) => {
    try {
      await updateTask(taskId, updateData);
      fetchData();
    } catch (error) {
      console.error('Erro ao atualizar tarefa:', error);
    }
  };

  const handleDelete = async (task) => {
    if (!window.confirm(`Excluir tarefa "${task.title}"?`)) return;
    try {
      await deleteTask(task.id);
      fetchData();
    } catch (error) {
      alert('Erro ao excluir tarefa');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await updateTask(taskId, { status: newStatus });
      fetchData();
    } catch (error) {
      alert('Erro ao atualizar status');
    }
  };

  const handleSaveStatus = async (statusData, statusId = null) => {
    if (statusId) {
      await updateTaskStatus(statusId, statusData);
    } else {
      await createTaskStatus(statusData);
    }
    fetchData();
  };

  const handleDeleteStatus = async (statusId) => {
    await deleteTaskStatus(statusId);
    fetchData();
  };

  const openModal = (task = null) => {
    setEditingTask(task);
    setModalOpen(true);
  };

  const getStatusInfo = (statusId) => {
    return (statuses || []).find(s => s.id === statusId) || { id: statusId, name: statusId, color: '#64748b' };
  };

  const filteredTasks = tasks.filter(task => {
    // Multi-status filter: show task if ANY of the selected statuses matches
    if (filterStatuses.length > 0 && !filterStatuses.includes(task.status)) return false;
    if (filterPriority !== 'all' && task.priority !== filterPriority) return false;
    // Multi-user filter: show task if ANY of the selected users is assigned
    if (filterUsers.length > 0 && !task.assigned_users?.some(userId => filterUsers.includes(userId))) return false;
    if (searchTerm && !task.title.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const getTasksByStatus = (statusId) => filteredTasks.filter(t => t.status === statusId);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
            <p className="text-sm text-slate-500">Total</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <p className="text-2xl font-bold text-slate-600">{stats.pendente}</p>
            <p className="text-sm text-slate-500">Pendente</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-blue-200 bg-blue-50">
            <p className="text-2xl font-bold text-blue-600">{stats.em_andamento}</p>
            <p className="text-sm text-blue-600">Em Andamento</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-amber-200 bg-amber-50">
            <p className="text-2xl font-bold text-amber-600">{stats.aguardando_aprovacao}</p>
            <p className="text-sm text-amber-600">Aguardando</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-green-200 bg-green-50">
            <p className="text-2xl font-bold text-green-600">{stats.concluida}</p>
            <p className="text-sm text-green-600">Concluída</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <p className="text-2xl font-bold text-slate-400">{stats.cancelada}</p>
            <p className="text-sm text-slate-400">Cancelada</p>
          </div>
          {stats.atrasadas > 0 && (
            <div className="bg-red-50 rounded-xl p-4 border border-red-200">
              <p className="text-2xl font-bold text-red-600">{stats.atrasadas}</p>
              <p className="text-sm text-red-600">Atrasadas</p>
            </div>
          )}
        </div>
      )}

      {/* Actions & Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="flex gap-2">
            <Button onClick={() => openModal()} className="bg-teal-600 hover:bg-teal-700 text-white">
              <Plus className="w-4 h-4 mr-2" />
              Nova Tarefa
            </Button>
            <Button onClick={() => setStatusModalOpen(true)} variant="outline">
              <Settings className="w-4 h-4 mr-2" />
              Configurar Status
            </Button>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-40"
              />
            </div>
            
            {/* Multi-select Status Filter */}
            <Popover open={showStatusFilter} onOpenChange={setShowStatusFilter}>
              <PopoverTrigger asChild>
                <button
                  className={`flex items-center gap-2 border rounded-lg px-3 py-2 text-sm transition-colors ${
                    filterStatuses.length > 0 
                      ? 'border-teal-300 bg-teal-50 text-teal-700' 
                      : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid="status-filter-trigger"
                >
                  <ListFilter className="w-4 h-4" />
                  {filterStatuses.length === 0 
                    ? 'Todos Status' 
                    : filterStatuses.length === 1 
                      ? statuses.find(s => s.id === filterStatuses[0])?.name || '1 selecionado'
                      : `${filterStatuses.length} selecionados`
                  }
                  <ChevronDown className="w-4 h-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-2" align="start">
                <div className="space-y-1">
                  <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Filtrar por Status
                  </div>
                  {filterStatuses.length > 0 && (
                    <button
                      onClick={() => setFilterStatuses([])}
                      className="w-full text-left px-2 py-1.5 text-xs text-teal-600 hover:bg-slate-100 rounded-md"
                      data-testid="status-filter-clear"
                    >
                      Limpar seleção
                    </button>
                  )}
                  <div className="border-t border-slate-100 my-1" />
                  {(statuses || []).map(status => (
                    <label
                      key={status.id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-100 cursor-pointer"
                      data-testid={`status-filter-option-${status.id}`}
                    >
                      <Checkbox
                        checked={filterStatuses.includes(status.id)}
                        onCheckedChange={() => toggleStatusFilter(status.id)}
                        className="h-4 w-4"
                      />
                      <span 
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: status.color || '#64748b' }}
                      />
                      <span className="text-sm text-slate-700">{status.name}</span>
                    </label>
                  ))}
                  {(statuses || []).length === 0 && (
                    <div className="text-xs text-slate-400 px-2 py-2">
                      Nenhum status disponível
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
            
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">Todas Prioridades</option>
              {priorityOptions.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            
            {/* Multi-select User Filter */}
            <Popover open={showUserFilter} onOpenChange={setShowUserFilter}>
              <PopoverTrigger asChild>
                <button
                  className={`flex items-center gap-2 border rounded-lg px-3 py-2 text-sm transition-colors ${
                    filterUsers.length > 0 
                      ? 'border-teal-300 bg-teal-50 text-teal-700' 
                      : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid="user-filter-trigger"
                >
                  <Users className="w-4 h-4" />
                  {filterUsers.length === 0 
                    ? 'Todos Responsáveis' 
                    : filterUsers.length === 1 
                      ? users.find(u => u.id === filterUsers[0])?.name || '1 selecionado'
                      : `${filterUsers.length} selecionados`
                  }
                  <ChevronDown className="w-4 h-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-2" align="start">
                <div className="space-y-1">
                  <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Filtrar por Responsável
                  </div>
                  {filterUsers.length > 0 && (
                    <button
                      onClick={() => setFilterUsers([])}
                      className="w-full text-left px-2 py-1.5 text-xs text-teal-600 hover:bg-slate-100 rounded-md"
                      data-testid="user-filter-clear"
                    >
                      Limpar seleção
                    </button>
                  )}
                  <div className="border-t border-slate-100 my-1" />
                  {users.map(user => (
                    <label
                      key={user.id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-100 cursor-pointer"
                      data-testid={`user-filter-option-${user.id}`}
                    >
                      <Checkbox
                        checked={filterUsers.includes(user.id)}
                        onCheckedChange={() => toggleUserFilter(user.id)}
                        className="h-4 w-4"
                      />
                      <span className="text-sm text-slate-700">{user.name}</span>
                    </label>
                  ))}
                  {users.length === 0 && (
                    <div className="text-xs text-slate-400 px-2 py-2">
                      Nenhum usuário disponível
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
            
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="kanban">Kanban</option>
              <option value="list">Lista</option>
            </select>
            
            {/* Save/Reset Filter Buttons */}
            <div className="flex items-center gap-1 border-l border-slate-200 pl-2 ml-1">
              <button
                onClick={handleSaveFilters}
                className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm transition-colors ${hasUnsavedChanges ? 'text-teal-600 hover:bg-teal-50 border border-teal-200' : 'text-slate-300 cursor-default border border-transparent'}`}
                title="Salvar filtros como padrão"
                disabled={!hasUnsavedChanges}
              >
                <Save className="w-4 h-4" />
                <span className="hidden md:inline">Salvar</span>
              </button>
              <button
                onClick={handleResetFilters}
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                title="Resetar filtros"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
              {saveMessage && (
                <span className="text-xs text-green-600 font-medium animate-pulse">{saveMessage}</span>
              )}
            </div>
            
            {/* Refresh & Connection Status */}
            <div className="flex items-center gap-1 border-l border-slate-200 pl-2 ml-1">
              <button
                onClick={handleManualRefresh}
                disabled={isRefreshing}
                className="p-1.5 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors disabled:opacity-50"
                title="Atualizar tarefas"
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
              <div 
                className={`p-1.5 rounded-lg ${wsConnected ? 'text-emerald-500' : 'text-slate-300'}`}
                title={wsConnected ? 'Tempo real ativo' : 'Tempo real desconectado'}
              >
                {wsConnected ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Kanban View */}
      {viewMode === 'kanban' ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {(statuses || []).filter(s => !s.is_final || getTasksByStatus(s.id).length > 0).map(status => {
            const statusTasks = getTasksByStatus(status.id);
            return (
              <div key={status.id} className="flex-shrink-0 w-80">
                <div 
                  className="rounded-xl p-3"
                  style={{ backgroundColor: `${status.color}15` }}
                >
                  <div className="flex items-center justify-between mb-3 px-2">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: status.color }}
                      />
                      <span className="font-medium text-sm" style={{ color: status.color }}>
                        {status.name}
                      </span>
                      {status.is_final && (
                        <span className="text-xs bg-slate-200 px-1.5 py-0.5 rounded">Final</span>
                      )}
                    </div>
                    <span 
                      className="text-xs font-bold px-2 py-0.5 rounded-full text-white"
                      style={{ backgroundColor: status.color }}
                    >
                      {statusTasks.length}
                    </span>
                  </div>
                  
                  <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                    {statusTasks.map(task => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        users={users}
                        statuses={statuses}
                        onEdit={openModal}
                        onDelete={handleDelete}
                        onStatusChange={handleStatusChange}
                      />
                    ))}
                    {statusTasks.length === 0 && (
                      <div className="text-center py-8 text-sm text-slate-400">
                        Nenhuma tarefa
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {filteredTasks.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nenhuma tarefa encontrada</p>
            </div>
          ) : (
            <>
              {/* Column width controls */}
              <div className="flex items-center justify-end gap-2 px-3 py-1.5 bg-slate-100 border-b border-slate-200">
                <span className="text-xs text-slate-500">Arraste as bordas das colunas para redimensionar</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={resetColumnWidths}
                  className="h-6 px-2 text-xs text-slate-500 hover:text-slate-700"
                  title="Restaurar larguras padrão"
                >
                  <RotateCcw className="w-3 h-3 mr-1" />
                  Reset
                </Button>
              </div>
              
              {/* Table Header - Resizable */}
              <div 
                className="flex items-center px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider select-none"
                style={{ cursor: resizing ? 'col-resize' : 'default' }}
              >
                <div className="flex items-center relative" style={{ width: columnWidths.titulo, minWidth: 100 }}>
                  <span className="truncate">Título</span>
                  <div 
                    className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-teal-300 transition-colors"
                    onMouseDown={(e) => handleMouseDown(e, 'titulo')}
                  />
                </div>
                <div className="flex items-center relative" style={{ width: columnWidths.criadoPor, minWidth: 50 }}>
                  <span className="truncate">Criado</span>
                  <div 
                    className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-teal-300 transition-colors"
                    onMouseDown={(e) => handleMouseDown(e, 'criadoPor')}
                  />
                </div>
                <div className="flex items-center relative" style={{ width: columnWidths.data, minWidth: 50 }}>
                  <span className="truncate">Data</span>
                  <div 
                    className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-teal-300 transition-colors"
                    onMouseDown={(e) => handleMouseDown(e, 'data')}
                  />
                </div>
                <div className="flex items-center relative" style={{ width: columnWidths.responsaveis, minWidth: 80 }}>
                  <span className="truncate">Responsáveis</span>
                  <div 
                    className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-teal-300 transition-colors"
                    onMouseDown={(e) => handleMouseDown(e, 'responsaveis')}
                  />
                </div>
                <div className="flex items-center relative" style={{ width: columnWidths.status, minWidth: 80 }}>
                  <span className="truncate">Status</span>
                  <div 
                    className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-teal-300 transition-colors"
                    onMouseDown={(e) => handleMouseDown(e, 'status')}
                  />
                </div>
                <div className="flex items-center justify-end flex-1" style={{ minWidth: columnWidths.acoes }}>
                  <span>Ações</span>
                </div>
              </div>
              
              {/* Table Body */}
              <div className="divide-y divide-slate-100">
                {filteredTasks.map(task => {
                  const status = getStatusInfo(task.status);
                  const priority = getPriorityInfo(task.priority);
                  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && !status.is_final;
                  
                  // Get assigned user names
                  const assignedNames = task.assigned_users?.map(userId => {
                    const user = users.find(u => u.id === userId);
                    return user?.name || user?.username || '';
                  }).filter(Boolean) || [];
                  
                  return (
                    <div 
                      key={task.id}
                      className={`flex items-center px-3 py-1.5 hover:bg-slate-50 transition-colors ${isOverdue ? 'bg-red-50' : ''}`}
                    >
                      {/* Título */}
                      <div className="flex items-center gap-1.5 min-w-0 pr-2" style={{ width: columnWidths.titulo }}>
                        <span style={{ color: priority.color }}>
                          <Flag className="w-3.5 h-3.5 flex-shrink-0" />
                        </span>
                        <h3 className="text-sm font-medium text-slate-800 truncate">{task.title}</h3>
                        {isOverdue && (
                          <AlertCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                        )}
                      </div>
                      
                      {/* Criado por */}
                      <div style={{ width: columnWidths.criadoPor }} className="pr-2">
                        <span className="text-xs text-slate-500 truncate block" title={task.created_by_name || '-'}>
                          {task.created_by_name ? task.created_by_name.split(' ')[0] : '-'}
                        </span>
                      </div>
                      
                      {/* Data Prevista */}
                      <div style={{ width: columnWidths.data }} className="pr-2">
                        {task.due_date ? (
                          <span className={`text-xs flex items-center gap-0.5 ${isOverdue ? 'text-red-600 font-medium' : 'text-slate-500'}`}>
                            <Calendar className="w-3 h-3" />
                            {new Date(task.due_date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </div>
                      
                      {/* Responsáveis - Dropdown editável */}
                      <div style={{ width: columnWidths.responsaveis }} className="pr-2">
                        <Popover>
                          <PopoverTrigger asChild>
                            <button 
                              className="w-full text-left text-xs border border-slate-200 rounded px-2 py-1 hover:bg-slate-50 flex items-center gap-1"
                              title={assignedNames.join(', ')}
                            >
                              <Users className="w-3 h-3 flex-shrink-0 text-slate-400" />
                              <span className="truncate flex-1">
                                {assignedNames.length === 0 
                                  ? 'Nenhum' 
                                  : assignedNames.map(n => n.split(' ')[0]).join(', ')}
                              </span>
                              <ChevronDown className="w-3 h-3 flex-shrink-0 text-slate-400" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-48 p-2" align="start">
                            <div className="space-y-1 max-h-48 overflow-y-auto">
                              {users.map(user => (
                                <label 
                                  key={user.id}
                                  className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 rounded cursor-pointer"
                                >
                                  <Checkbox
                                    checked={task.assigned_users?.includes(user.id) || false}
                                    onCheckedChange={(checked) => {
                                      const newAssigned = checked
                                        ? [...(task.assigned_users || []), user.id]
                                        : (task.assigned_users || []).filter(id => id !== user.id);
                                      handleInlineUpdate(task.id, { assigned_users: newAssigned });
                                    }}
                                  />
                                  <span className="text-sm truncate">{user.name || user.username}</span>
                                </label>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                      
                      {/* Status */}
                      <div style={{ width: columnWidths.status }} className="pr-2">
                        <select
                          value={task.status}
                          onChange={(e) => handleStatusChange(task.id, e.target.value)}
                          className="text-xs border rounded px-1.5 py-1 w-full"
                          style={{ borderColor: status.color, color: status.color }}
                        >
                          {(statuses || []).map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Ações */}
                      <div className="flex items-center justify-end gap-0.5 flex-1" style={{ minWidth: columnWidths.acoes }}>
                        <Button variant="ghost" size="sm" onClick={() => openModal(task)} className="h-7 w-7 p-0">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(task)} className="h-7 w-7 p-0 text-red-500 hover:text-red-600">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Task Modal */}
      <TaskModal
        isOpen={modalOpen}
        task={editingTask}
        users={users}
        statuses={statuses}
        prefilledContact={prefilledContact}
        onClose={() => {
          setModalOpen(false);
          setEditingTask(null);
        }}
        onSave={handleSave}
        onClearContact={() => {
          setPrefilledContact(null);
          if (onTaskCreatedFromContact) onTaskCreatedFromContact();
        }}
      />

      {/* Status Configuration Modal */}
      <StatusConfigModal
        isOpen={statusModalOpen}
        statuses={statuses}
        onClose={() => setStatusModalOpen(false)}
        onSave={handleSaveStatus}
        onDelete={handleDeleteStatus}
      />
    </div>
  );
};

export default TasksSection;
